import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { STATS, HUMAN_COST } from "../data/content";

const ease = [0.16, 1, 0.3, 1];
const toneClass = { orange: "text-african", gold: "text-gold", green: "text-safety" };

const Counter = ({ value, prefix = "", suffix = "", decimals = 0, start }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!start) return;
    const ctrl = animate(0, value, { duration: 2.2, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setN(v) });
    return () => ctrl.stop();
  }, [start, value]);
  return (
    <>
      {prefix}
      {n.toFixed(decimals).replace(".", ",")}
      {suffix}
    </>
  );
};

const StatCard = ({ s, i, start }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay: i * 0.12, ease }}
    className="relative border-l border-white/15 pl-6 py-2"
    data-testid={`stat-counter-${s.id}`}
  >
    <div className={`font-heading font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-none tabular-nums ${toneClass[s.tone]}`}>
      <Counter value={s.value} prefix={s.prefix} suffix={s.suffix} decimals={s.decimals} start={start} />
    </div>
    <p className="mt-4 text-white/85 text-sm sm:text-base leading-snug max-w-[16rem]">{s.label}</p>
    <p className="mt-2 font-accent text-[0.7rem] tracking-[0.25em] uppercase text-white/40">{s.source}</p>
  </motion.div>
);

const CostCard = ({ c, i }) => (
  <motion.article
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay: i * 0.15, ease }}
    whileHover={{ y: -6 }}
    className="group relative rounded-[2rem] bg-white/[0.06] border border-white/10 p-8 backdrop-blur-md overflow-hidden"
    data-testid={`human-cost-${i}`}
  >
    <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-african/20 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
    <div className="font-heading font-extrabold text-4xl text-gold leading-none">{c.figure}</div>
    <div className="font-accent text-[0.7rem] tracking-[0.25em] uppercase text-white/50 mt-2">{c.unit}</div>
    <h3 className="font-heading font-bold text-xl text-white mt-6">{c.title}</h3>
    <p className="mt-3 text-white/70 text-sm leading-relaxed">{c.text}</p>
  </motion.article>
);

export const Stats = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });

  return (
    <section id="impact" ref={ref} className="relative bg-forest py-24 lg:py-32 overflow-hidden grain" data-testid="stats-section">
      <div className="absolute inset-0 dots opacity-40 [background-image:radial-gradient(rgba(255,255,255,0.12)_1px,transparent_1px)]" />
      <div className="absolute -top-40 right-0 w-[700px] h-[700px] rounded-full bg-safety/20 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16 lg:mb-24">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-5 text-gold">L'urgence en chiffres</p>
            <h2 className="font-heading font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.02] tracking-tight">
              Le travail ne devrait jamais <span className="text-african">coûter une vie.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-white/65 text-base leading-relaxed lg:pb-2">
            Les accidents et maladies professionnels tuent plus que les guerres et les accidents de la route réunis. Et la grande majorité est évitable.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
          {STATS.map((s, i) => <StatCard key={s.id} s={s} i={i} start={inView} />)}
        </div>

        <div className="mt-20 lg:mt-28 grid md:grid-cols-3 gap-6">
          {HUMAN_COST.map((c, i) => <CostCard key={c.title} c={c} i={i} />)}
        </div>
      </div>
    </section>
  );
};
